import { MdsHoverContentGroup } from "@/design-system/components/hover-content/MdsHoverContentGroup";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";
import { mdsSidebarWidths } from "@/design-system/foundations";
import styled from "@emotion/styled";
import { MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import { MDSIconButton, MDSIconButtonSize, MDSIconButtonVariant } from "@/mds/components";
import {
  MdsHoverContent,
  MdsHoverContentPlacement,
} from "@/design-system/components/hover-content/MdsHoverContent";
import { AccountProfileImageContent } from "@/components/layout/components/account-profile/AccountProfileImageContent";
import { ProfileSize } from "@/components/layout/components/account-profile";
import { SidebarDropdownMenu } from "@/components/layout/components/sidebar/SidebarDropdownMenu";
import { useMatch } from "react-router";
import { appRoutes } from "@/app/router";
import { ProfileVariant } from "@/components/layout/components/account-profile/types";
import { DebugModeTapDetector } from "../../account-profile/DebugModeTapDetector";
import {
  StyledAccountInfoWrapper,
  StyledSidebarWrapper,
} from "@/components/layout/components/sidebar/sidebar.styles";
import { EventContext } from "@/domains/metrics/context";
import { SidebarContentsRecentCollectionsList } from "@/components/layout/components/sidebar/sidebar-contents/sidebar-contents-recent-collections";
import { SidebarContentsPinnedItemsList } from "@/components/layout/components/sidebar/sidebar-contents/sidebar-contents-pinned-items";
import { SidebarContentsRecentNotesList } from "@/components/layout/components/sidebar/sidebar-contents/sidebar-contents-recent-notes";
import { NoteObservable } from "@/store/note/NoteObservable";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { isMac } from "@/domains/platform/isMac";
import { clientEnvModule } from "@/modules/client-env";

interface SidebarCollapsedProps {
  setIsImportModalOpen: (b: boolean) => void;
  toggleSidebar: () => void;
  shouldHideSidebarToggle: boolean;
}

export const SidebarCollapsed = observer(
  ({ setIsImportModalOpen, toggleSidebar, shouldHideSidebarToggle }: SidebarCollapsedProps) => {
    const { store } = useAppStore();
    const isMacOS = isMac();
    const isDesktop = clientEnvModule.isDesktop();

    const isNotesPath = useMatch(appRoutes.notesList({}));
    const isCollectionsPath = useMatch(appRoutes.collections({}));

    const isTouchDevice = store.interface.isTouchDevice;
    const { profileDisplayName, profilePhotoUrl } = store.account.myAccount;

    const hoverGroup = useMemo(() => new MdsHoverContentGroup(), []);
    const closeAll = useCallback(() => hoverGroup.hideAll({}), [hoverGroup]);

    const hasPinnedItems = store.favoriteItems.sortedFavoriteItems.length > 0;
    const hasRecentNotes =
      store.recentItems.sortedRecentItems.filter(item => item instanceof NoteObservable).length > 0;
    const hasRecentCollections =
      store.recentItems.sortedRecentItems.filter(item => item instanceof CollectionObservable)
        .length > 0;

    return (
      <StyledSidebarCollapsedWrapper $avoidMacOsTrafficLights={isMacOS && isDesktop}>
        <MdsHoverContent
          group={hoverGroup}
          content={() => (
            <SidebarDropdownMenu
              displayAccountInfo
              setIsImportModalOpen={setIsImportModalOpen}
              onClick={closeAll}
            />
          )}
          placement={MdsHoverContentPlacement.Right}
        >
          <StyledAccountInfoWrapper>
            <DebugModeTapDetector>
              <AccountProfileImageContent
                size={ProfileSize.Standard}
                photoUrl={profilePhotoUrl}
                displayName={profileDisplayName}
                variant={ProfileVariant.Rounded}
                showSyncStatus
              />
            </DebugModeTapDetector>
          </StyledAccountInfoWrapper>
        </MdsHoverContent>

        <MDSIconButton
          size={MDSIconButtonSize.Medium}
          iconKind={MdsIconKind.ComposeInline}
          variant={MDSIconButtonVariant.Chrome}
          onClick={() => store.notes.composeNewNote({ eventContext: EventContext.GlobalActions })}
          rounded
        />

        <StyledSidebarContentContainer>
          <MdsHoverContent
            group={hoverGroup}
            content={() =>
              hasPinnedItems ? (
                <StyledHoverContentContainer>
                  <SidebarContentsPinnedItemsList includeTitle onClick={closeAll} />
                </StyledHoverContentContainer>
              ) : null
            }
            placement={MdsHoverContentPlacement.Right}
          >
            <StyledIconButton
              iconKind={MdsIconKind.Thumbtack}
              variant={MDSIconButtonVariant.OnChrome}
              isDisabled={!hasPinnedItems}
            />
          </MdsHoverContent>
          <MdsHoverContent
            isTouchDevice={isTouchDevice}
            group={hoverGroup}
            content={() =>
              hasRecentNotes ? (
                <StyledHoverContentContainer>
                  <SidebarContentsRecentNotesList includeTitle onClick={closeAll} />
                </StyledHoverContentContainer>
              ) : null
            }
            placement={MdsHoverContentPlacement.Right}
          >
            <StyledIconButton
              iconKind={MdsIconKind.Notebook}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={event => store.navigation.goToNotes({ event })}
              $isActive={!!isNotesPath}
            />
          </MdsHoverContent>
          <MdsHoverContent
            isTouchDevice={isTouchDevice}
            group={hoverGroup}
            content={() =>
              hasRecentCollections ? (
                <StyledHoverContentContainer>
                  <SidebarContentsRecentCollectionsList includeTitle onClick={closeAll} />
                </StyledHoverContentContainer>
              ) : null
            }
            placement={MdsHoverContentPlacement.Right}
          >
            <StyledIconButton
              iconKind={MdsIconKind.Collection}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={event => store.navigation.goToCollections({ event })}
              $isActive={!!isCollectionsPath}
            />
          </MdsHoverContent>
        </StyledSidebarContentContainer>
        {!shouldHideSidebarToggle && (
          <StyledSidebarToggleButton
            size={MDSIconButtonSize.Medium}
            iconKind={MdsIconKind.ArrowRightToLine}
            variant={MDSIconButtonVariant.Transparent}
            onClick={() => toggleSidebar()}
          />
        )}
      </StyledSidebarCollapsedWrapper>
    );
  }
);

const StyledSidebarCollapsedWrapper = styled(StyledSidebarWrapper)`
  height: 100%;
  align-items: center;
  width: ${mdsSidebarWidths().collapsed}px;
  flex: 1;
  min-height: 0;
`;
StyledSidebarCollapsedWrapper.displayName = "StyledSidebarCollapsedWrapper";

const StyledSidebarContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-top: ${({ theme }) => theme.spacing["4"]};
`;
StyledSidebarContentContainer.displayName = "StyledSidebarContentContainer";

const StyledIconButton = styled(MDSIconButton)<{ $isActive?: boolean }>`
  background-color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.colors.background.button.onChrome.hover
      : theme.colors.background.button.onChrome.default};
`;
StyledIconButton.displayName = "StyledIconButton";

const StyledSidebarToggleButton = styled(MDSIconButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background.button.navigation.default};
  border-radius: ${({ theme }) => theme.borderRadius.none};
  border-top-left-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.xlarge};

  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.button.navigation.hover};
  }
`;
StyledSidebarToggleButton.displayName = "StyledSidebarToggleButton";

const StyledHoverContentContainer = styled.div`
  padding-block: ${({ theme }) => theme.spacing["4"]};
  padding-inline: ${({ theme }) => theme.spacing["3"]};
  background-color: ${({ theme }) => theme.colors.surface.popover};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  width: 288px;
`;
StyledHoverContentContainer.displayName = "StyledHoverContentContainer";
